import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';

// Module-level set so we don't re-POST the same token on Strict Mode remount.
const attemptedTokens = new Set();

export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink, isAuthenticated } = useAuth();

  const token = params.get('token');
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const inProgress = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('No token provided in the URL.');
      return;
    }

    // If we're already authenticated (e.g. user clicked the link in the same
    // tab they're already signed into), skip re-verification.
    if (isAuthenticated) {
      setStatus('success');
      navigate('/', { replace: true });
      return;
    }

    if (attemptedTokens.has(token) || inProgress.current) return;
    attemptedTokens.add(token);
    inProgress.current = true;

    (async () => {
      const res = await verifyMagicLink(token);
      inProgress.current = false;
      if (res.success) {
        setStatus('success');
        // Brief pause so the user sees the success state, then to overview.
        setTimeout(() => navigate('/', { replace: true }), 800);
      } else {
        setStatus('error');
        setError(res.error || 'Verification failed.');
      }
    })();
  }, [token, isAuthenticated, navigate, verifyMagicLink]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-5 border-b border-stone-900/80">
        <Link to="/login" className="flex items-center gap-2 w-fit">
          <span className="text-alfred-gold font-serif text-2xl tracking-wide">
            Alfred
          </span>
          <span className="text-xs uppercase tracking-[0.25em] text-stone-500">
            Voice
          </span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="card text-center">
            {status === 'verifying' && (
              <>
                <Loader2
                  className="mx-auto text-alfred-gold animate-spin"
                  size={32}
                />
                <h1 className="mt-4 text-2xl text-stone-100">Verifying…</h1>
                <p className="mt-1 text-sm text-stone-400">
                  Hang tight while we sign you in.
                </p>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-900/40 border border-emerald-800">
                  <CheckCircle2 className="text-emerald-300" size={22} />
                </div>
                <h1 className="mt-4 text-2xl text-stone-100">Signed in</h1>
                <p className="mt-1 text-sm text-stone-400">
                  Redirecting to your dashboard…
                </p>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-rose-900/40 border border-rose-800">
                  <AlertCircle className="text-rose-300" size={22} />
                </div>
                <h1 className="mt-4 text-2xl text-stone-100">
                  Couldn't sign you in
                </h1>
                <p className="mt-1 text-sm text-stone-400">{error}</p>
                <Link to="/login" className="btn-primary mt-6">
                  Back to sign-in
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
